import {
  ActionIcon,
  Button,
  Card,
  Group,
  Stack,
  Switch,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { Plus, Trash2 } from 'react-feather';
import { RichTextEditor } from '../../../../../components/common/RichTextEditor/RichTextEditor';
import type {
  SessionAgendaPreSessionWork,
  SessionAgendaReflectionPrompt,
} from '../../../../../services/api/sessionAgenda';

/**
 * MED-981 (E1.3f) — Participant Pre-Session Work panel.
 *
 * Sits below the Agenda Sections panel in the Session Content editor.
 * Configures what participants complete BEFORE the session — three fields,
 * in the order the participant pre-session page (E3.1c) will render them:
 *
 *   1. Enable Commitment Review (toggle, AC-CR)
 *      - Helper: "Participants review their previous session's commitment."
 *      - Gated UI-side on cohortCommitmentTrackingEnabled (hidden when off,
 *        consistent with e1.3e per-section toggles).
 *
 *   2. Reflection Prompts (repeatable plain-text, AC-RP)
 *      - Minimum 2 non-empty required for session completeness.
 *      - "Add Prompt" / trash icon affordances; order persists.
 *      - NOT rich text per the spec.
 *
 *   3. Reading / Tasks (rich text, AC-RT)
 *      - Bold / italic / underline / bullet list.
 *      - Optional; placeholder "Add reading materials, videos, or tasks for
 *        participants…".
 *      - Note: the shared RichTextEditor toolbar is a superset (also
 *        H1-H3, blockquote, link, strikethrough). Documented as a known
 *        limitation in stories/MED-975.md and inherited here — trimming for
 *        this specific surface would be a separate concern.
 *
 * The panel is presentational — draft state and persistence live in the
 * parent SessionContentEditorPage so a single Save commits the entire
 * editor.
 *
 * Re-order vs. the beta layout: the beta had Reading/Tasks before Reflection
 * Prompts. SGOS deliberately re-orders to match the participant pre-session
 * page (E3.1c). The CM authors in the same order participants experience.
 */
export interface ParticipantPreSessionWorkPanelProps {
  value: SessionAgendaPreSessionWork;
  onChange: (next: SessionAgendaPreSessionWork) => void;
  /**
   * MED-981 AC-CR2: when false, the Commitment Review toggle is hidden
   * entirely (not greyed). Same UI gate as e1.3e per-section toggles —
   * driven by cohorts.commitment_tracking_enabled.
   */
  cohortCommitmentTrackingEnabled: boolean;
}

export function ParticipantPreSessionWorkPanel({
  value,
  onChange,
  cohortCommitmentTrackingEnabled,
}: ParticipantPreSessionWorkPanelProps) {
  const addPrompt = () => {
    const next: SessionAgendaReflectionPrompt = {
      prompt_text: null,
      sort_order: value.reflection_prompts.length + 1,
    };
    onChange({
      ...value,
      reflection_prompts: [...value.reflection_prompts, next],
    });
  };

  const updatePrompt = (index: number, text: string) => {
    onChange({
      ...value,
      reflection_prompts: value.reflection_prompts.map((p, i) =>
        i === index ? { ...p, prompt_text: text || null } : p,
      ),
    });
  };

  const removePrompt = (index: number) => {
    onChange({
      ...value,
      reflection_prompts: value.reflection_prompts
        .filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, sort_order: i + 1 })),
    });
  };

  // MED-1182 (E1.3f-R1) — reflection prompts are OPTIONAL. There is no longer
  // a minimum; the panel just reports how many non-empty prompts are authored
  // (0 is a valid, complete state).
  const nonEmptyPromptCount = value.reflection_prompts.filter(
    p => p.prompt_text !== null && p.prompt_text.trim().length > 0,
  ).length;

  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      data-testid="agenda-participant-pre-session-work-panel"
    >
      <Stack gap={4} mb="md">
        <Title order={3}>Participant Pre-Session Work</Title>
        <Text size="sm" c="dimmed">
          Configure what participants complete before this session.
        </Text>
      </Stack>

      <Stack gap="lg">
        {/* AC-PANEL2: field order is Commitment Review → Reflection Prompts
            → Reading/Tasks (matches the participant page E3.1c order, NOT
            the beta order). */}

        {/* 1. Enable Commitment Review — AC-CR1/CR2/CR3 */}
        {cohortCommitmentTrackingEnabled && (
          <Switch
            label="Enable Commitment Review"
            description="Participants review their previous session's commitment."
            checked={value.commitment_review_enabled}
            onChange={e =>
              onChange({ ...value, commitment_review_enabled: e.currentTarget.checked })
            }
            data-testid="agenda-pre-session-commitment-review-toggle"
          />
        )}

        {/* 2. Reflection Prompts — AC-RP1/RP2/RP3 */}
        <Stack gap="sm" data-testid="agenda-reflection-prompts">
          <Group justify="space-between" align="center">
            <Stack gap={0}>
              <Text fw={600} size="sm">
                Reflection Prompts
              </Text>
              <Text size="xs" c="dimmed">
                {nonEmptyPromptCount === 0
                  ? 'Optional — add prompts if participants should reflect before this session.'
                  : `${nonEmptyPromptCount} prompt${nonEmptyPromptCount === 1 ? '' : 's'}.`}
              </Text>
            </Stack>
            <Button
              leftSection={<Plus size={14} />}
              variant="default"
              size="xs"
              onClick={addPrompt}
              data-testid="agenda-add-reflection-prompt"
            >
              Add Prompt
            </Button>
          </Group>

          {value.reflection_prompts.length === 0 ? (
            <Text size="xs" c="dimmed" fs="italic">
              No reflection prompts yet. These are optional — add them if
              participants should reflect before this session.
            </Text>
          ) : (
            <Stack gap="xs">
              {value.reflection_prompts.map((prompt, i) => (
                <Group
                  key={prompt.id ?? `new-${i}`}
                  align="flex-start"
                  gap="xs"
                  wrap="nowrap"
                >
                  <Textarea
                    flex={1}
                    autosize
                    minRows={2}
                    placeholder={`Prompt ${i + 1} — e.g., "What's on your mind coming into this session?"`}
                    value={prompt.prompt_text ?? ''}
                    onChange={e => updatePrompt(i, e.currentTarget.value)}
                    data-testid={`agenda-reflection-prompt-${i}`}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="dangerRed"
                    onClick={() => removePrompt(i)}
                    aria-label={`Delete prompt ${i + 1}`}
                    mt={4}
                    data-testid={`agenda-delete-reflection-prompt-${i}`}
                  >
                    <Trash2 size={16} />
                  </ActionIcon>
                </Group>
              ))}
              {/* MED-1073 (E1.3i / AC-AP2, AC-AP3, same-class affordance) —
                  duplicate Add Prompt at the bottom of the reflection-prompts
                  list, matching the Leader Prompts treatment. Identical
                  behavior to the top button; CMs add ≥2 prompts here often, so
                  the bottom-append spares the scroll back to the top. */}
              <Group justify="flex-end">
                <Button
                  leftSection={<Plus size={14} />}
                  variant="default"
                  size="xs"
                  onClick={addPrompt}
                  data-testid="agenda-add-reflection-prompt-bottom"
                >
                  Add Prompt
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>

        {/* 3. Reading / Tasks — AC-RT1/RT2 */}
        <Stack gap="xs">
          <RichTextEditor
            label="Reading / Tasks"
            placeholder="Add reading materials, videos, or tasks for participants…"
            value={value.reading_tasks ?? ''}
            onChange={html => onChange({ ...value, reading_tasks: html || null })}
            minHeight={120}
          />
          <Text size="xs" c="dimmed">
            Optional. Use bold, italic, underline, or bullet list for emphasis.
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}
